import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ApiError } from '../../lib/api';
import { useCurrentUser } from '../auth';
import type {
  XeroContactMappingRow,
  XeroPickableAccount,
  XeroTenantConnection,
} from '../../types/xero';
import {
  clearXeroContactMapping,
  getXeroConnections,
  getXeroContactMappings,
  setXeroContactMapping,
} from './api';

type StatusFilter = 'all' | 'unmapped' | 'mapped' | 'suggested';

// Admin-only contact→account mapping. List a Xero org's customer contacts with a
// name-match suggestion + current mapping; set/clear the mapping (writes
// xeroContactId onto the kyck account, audited, then re-derives that account's
// revenue rollups).
export function XeroMappingPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('unmapped');

  const connections = useQuery({
    queryKey: ['xero', 'connections'],
    queryFn: getXeroConnections,
    enabled: me.role === 'admin',
  });

  const orgs: XeroTenantConnection[] = connections.data?.connections ?? [];
  const activeTenant = tenantId ?? orgs[0]?.tenantId ?? null;

  const mapping = useQuery({
    queryKey: ['xero', 'mapping', activeTenant],
    queryFn: () => getXeroContactMappings(activeTenant as string),
    enabled: me.role === 'admin' && !!activeTenant,
  });

  const mappingKey = ['xero', 'mapping', activeTenant] as const;

  const setMap = useMutation({
    mutationFn: setXeroContactMapping,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mappingKey });
      toast.success('Mapped. Revenue re-derived for that account.');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't set the mapping."),
  });

  const clearMap = useMutation({
    mutationFn: clearXeroContactMapping,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mappingKey });
      toast.success('Unmapped.');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't unmap."),
  });

  const rows = mapping.data?.rows ?? [];
  const accounts = mapping.data?.accounts ?? [];
  const summary = mapping.data?.summary;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status === 'mapped' && !r.mappedAccountId) return false;
      if (status === 'unmapped' && r.mappedAccountId) return false;
      if (status === 'suggested' && (r.mappedAccountId || !r.suggestedAccountId)) return false;
      if (q && !(r.contactName ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, status]);

  if (me.role !== 'admin') {
    return <p className="text-sm text-slate-600">Only admins can map Xero contacts.</p>;
  }

  const busy = setMap.isPending || clearMap.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Xero contact mapping"
        description="Link each Xero contact to a kyck account so its invoiced revenue rolls up correctly."
        backHref="/settings/xero"
      />

      {connections.isLoading ? (
        <CenteredSpinner />
      ) : orgs.length === 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          No Xero organisations connected. Connect one on the{' '}
          <a href="/settings/xero" className="font-medium underline">
            Xero connections
          </a>{' '}
          page first.
        </div>
      ) : (
        <>
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {orgs.length > 1 && (
              <select
                value={activeTenant ?? ''}
                onChange={(e) => setTenantId(e.target.value)}
                className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
              >
                {orgs.map((o) => (
                  <option key={o.tenantId} value={o.tenantId}>
                    {o.tenantName ?? o.tenantId}
                  </option>
                ))}
              </select>
            )}
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contacts…"
              className="h-9 w-64 rounded-md border border-slate-300 bg-white px-3 text-sm"
            />
            <div className="flex gap-1">
              {(['unmapped', 'suggested', 'mapped', 'all'] as StatusFilter[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={[
                    'rounded-md px-3 py-1.5 text-sm font-medium capitalize',
                    status === s ? 'bg-kyck-50 text-kyck-700' : 'text-slate-600 hover:bg-slate-100',
                  ].join(' ')}
                >
                  {s}
                </button>
              ))}
            </div>
            {summary && (
              <span className="ml-auto text-sm text-slate-500">
                {summary.mapped} mapped · {summary.unmapped} unmapped · {summary.suggested} suggested
              </span>
            )}
          </div>

          {mapping.isLoading ? (
            <CenteredSpinner />
          ) : mapping.error ? (
            <p className="text-sm text-red-600">
              Couldn't load contacts. The org may need reconnecting.
            </p>
          ) : (
            <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5">Xero contact</th>
                    <th className="px-4 py-2.5">Invoices</th>
                    <th className="px-4 py-2.5">Mapped account</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                        No contacts match.
                      </td>
                    </tr>
                  ) : (
                    filtered.slice(0, 500).map((r) => (
                      <MappingRow
                        key={r.contactId}
                        row={r}
                        accounts={accounts}
                        busy={busy}
                        onMap={(accountId) =>
                          setMap.mutate({ tenantId: activeTenant as string, contactId: r.contactId, accountId })
                        }
                        onClear={() =>
                          clearMap.mutate({ tenantId: activeTenant as string, contactId: r.contactId })
                        }
                      />
                    ))
                  )}
                </tbody>
              </table>
              {filtered.length > 500 && (
                <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
                  Showing the first 500 of {filtered.length}. Narrow with search.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MappingRow({
  row,
  accounts,
  busy,
  onMap,
  onClear,
}: {
  row: XeroContactMappingRow;
  accounts: XeroPickableAccount[];
  busy: boolean;
  onMap: (accountId: string) => void;
  onClear: () => void;
}) {
  return (
    <tr>
      <td className="px-4 py-3 font-medium text-slate-900">{row.contactName ?? row.contactId}</td>
      <td className="px-4 py-3 text-slate-500">{row.invoiceCount || '—'}</td>
      <td className="px-4 py-3">
        {row.mappedAccountId ? (
          <span className="inline-flex items-center rounded-full bg-kyck-50 px-2 py-0.5 text-xs font-medium text-kyck-700">
            {row.mappedAccountName ?? row.mappedAccountId}
          </span>
        ) : row.suggestedAccountId ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onMap(row.suggestedAccountId as string)}
            className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
            title={`Suggested match (score ${row.suggestedScore?.toFixed(2)})`}
          >
            <Sparkles className="h-3 w-3" />
            {row.suggestedAccountName}
          </button>
        ) : (
          <span className="text-xs text-slate-400">Unmapped</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <AccountPicker
            accounts={accounts}
            selectedId={row.mappedAccountId}
            onPick={onMap}
            disabled={busy}
          />
          {row.mappedAccountId && (
            <Button variant="ghost" size="sm" onClick={onClear} disabled={busy} title="Unmap">
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function AccountPicker({
  accounts,
  selectedId,
  onPick,
  disabled,
}: {
  accounts: XeroPickableAccount[];
  selectedId: string | null;
  onPick: (accountId: string) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          {selectedId ? 'Change' : 'Map…'}
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <Command>
          <CommandInput placeholder="Search accounts…" />
          <CommandList>
            <CommandEmpty>No accounts.</CommandEmpty>
            <CommandGroup>
              {accounts.map((a) => (
                <CommandItem
                  key={a.id}
                  value={a.name}
                  onSelect={() => {
                    onPick(a.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={['mr-2 h-4 w-4', a.id === selectedId ? 'opacity-100' : 'opacity-0'].join(' ')}
                  />
                  <span className="flex-1">{a.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
